import React, { createContext, useContext, useState, useEffect } from "react";
import { getUserPermissions } from "../services/userPermission.service";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "./AuthProvider";
import { isAdminRole, isOrgEmployee, ROLES, ORG_ADMIN_PERMISSIONS, ORG_EMPLOYEE_PERMISSIONS } from "../utils/roles";

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within PermissionProvider");
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const { accessToken } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!accessToken) {
        setLoading(false);
        return;
      }

      try {
        const decoded = jwtDecode(accessToken);
        const userId = decoded.id;
        const isAdmin = isAdminRole(decoded.role);

        // Org Admin is an admin of its own org only — it gets a fixed, narrower
        // set that matches the backend allowlist, not the blanket admin grant.
        if (decoded.role === ROLES.ORG_ADMIN) {
          setPermissions(ORG_ADMIN_PERMISSIONS);
          setLoading(false);
          return;
        }

        // Employees created by an Org Admin: App Download only.
        if (isOrgEmployee(decoded.role, decoded.orgId)) {
          setPermissions(ORG_EMPLOYEE_PERMISSIONS);
          setLoading(false);
          return;
        }

        // If admin, grant all permissions
        if (isAdmin) {
          const allPermissions = {
            dashboard: { view: true, add: true, edit: true, delete: true },
            employee: { view: true, add: true, edit: true, delete: true },
            manager: { view: true, add: true, edit: true, delete: true },
            taskManagement: { view: true, add: true, edit: true, delete: true },
            parentTask: { view: true, add: true, edit: true, delete: true },
            todoTask: { view: true, add: true, edit: true, delete: true },
            client: { view: true, add: true, edit: true, delete: true },
            project: { view: true, add: true, edit: true, delete: true },
            clientTaskManagement: { view: true, add: true, edit: true, delete: true },
            timeSheet: { view: true, add: true, edit: true, delete: true },
            supportingFileUpload: { view: true, add: true, edit: true, delete: true },
            clientTaskReport: { view: true, add: true, edit: true, delete: true },
            leaveManagement: { view: true, add: true, edit: true, delete: true },
            leaveSummary: { view: true, add: true, edit: true, delete: true },
            earnedLeaveRequests: { view: true, add: true, edit: true, delete: true },
            myEarnedLeaveRequests: { view: true, add: true, edit: true, delete: true },
            taskreport: { view: true, add: true, edit: true, delete: true },
            publicHoliday: { view: true, add: true, edit: true, delete: true },
            attendance: { view: true, add: true, edit: true, delete: true },
            timeTracker: { view: true, add: true, edit: true, delete: true },
            projectReport: { view: true, add: true, edit: true, delete: true },
            projectWorkingReport: { view: true, add: true, edit: true, delete: true },
            parentTaskReport: { view: true, add: true, edit: true, delete: true },
            dailyWorkingReport: { view: true, add: true, edit: true, delete: true },
            candidateHistory: { view: true, add: true, edit: true, delete: true },
            reverted: { view: true, add: true, edit: true, delete: true },
            request: { view: true, add: true, edit: true, delete: true },
            shifts: { view: true, add: true, edit: true, delete: true },
            payrollManagement: { view: true, add: true, edit: true, delete: true },
            issuewisereport: { view: true, add: true, edit: true, delete: true },
            timeLoggerAttendance: { view: true, add: true, edit: true, delete: true },
            timeLoggerOrgs: { view: true, add: true, edit: true, delete: true },
            appDownload: { view: true, add: true, edit: true, delete: true },
          };
          setPermissions(allPermissions);
          setLoading(false);
          return;
        }

        // Fetch permissions for regular users
        const res = await getUserPermissions(userId);

        if (res?.data?.permissions) {
          setPermissions(res.data.permissions);
        } else {
          // No permissions set, default to empty
          setPermissions({});
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
        setPermissions({});
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [accessToken]);

  const hasPermission = (module, action) => {
    if (!permissions || !permissions[module]) return false;
    return permissions[module][action] === true;
  };

  const canView = (module) => hasPermission(module, "view");
  const canAdd = (module) => hasPermission(module, "add");
  const canEdit = (module) => hasPermission(module, "edit");
  const canDelete = (module) => hasPermission(module, "delete");

  const value = {
    permissions,
    loading,
    hasPermission,
    canView,
    canAdd,
    canEdit,
    canDelete,
    refreshPermissions: () => {
      setLoading(true);
      // Trigger re-fetch by changing accessToken dependency
    },
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
